import { useState, useRef, useCallback, useEffect } from 'react';
import { GridApi, IDatasource, IGetRowsParams } from 'ag-grid-community';

import Session from 'modules/Session';
import { fetchSetupList, fetchSetupListV2 } from 'modules/api/setup';
import { isCanceledError } from 'modules/api/request';
import { cancelFunctions } from 'utils/cancelFunctions';
import { getValidPageSize } from 'utils/pageSize';
import { useSetupPollDeltas } from './useSetupPollDeltas';
import { hasPollingStateRows } from './setupUtils';

interface UseSetupTableProps {
  pageSizeKey: string;
  isAssetTemplateV2Enabled: boolean | null;
}

interface UseSetupTableResult {
  tableApiRef: React.MutableRefObject<GridApi | undefined>;
  getTableApi: (tableApi: GridApi) => void;
  onPageSizeChange: (numberOfItems: number) => void;
  refreshTable: () => void;
  pageSize: number;
}

export const useSetupTable = ({ pageSizeKey, isAssetTemplateV2Enabled }: UseSetupTableProps): UseSetupTableResult => {
  const [pageSize, setPageSize] = useState(getValidPageSize(Session.getPageSizeByKey(pageSizeKey)));
  const tableApiRef = useRef<GridApi>();
  const getTableDataSourceRef = useRef<() => IDatasource>();
  const isAssetTemplateV2EnabledRef = useRef(isAssetTemplateV2Enabled);
  isAssetTemplateV2EnabledRef.current = isAssetTemplateV2Enabled;

  const { startPolling } = useSetupPollDeltas({
    isAssetTemplateV2Enabled,
    tableApiRef,
  });

  getTableDataSourceRef.current = (): IDatasource => {
    return {
      getRows: async (params: IGetRowsParams) => {
        if (isAssetTemplateV2Enabled === null) {
          return;
        }

        cancelFunctions.SETUPLIST_DATA_URL?.();
        cancelFunctions.SETUPLIST_DATA_URL_V2?.();

        const pageNumber = params.startRow / pageSize;

        const fetchSetupListRequest = isAssetTemplateV2Enabled ? fetchSetupListV2 : fetchSetupList;

        try {
          const { setupList, totalElements } = await fetchSetupListRequest(
            cancelFunctions,
            `&pageNo=${pageNumber}&pageSize=${pageSize}`,
          );

          if (Number(totalElements) === 0) {
            tableApiRef.current?.showNoRowsOverlay();
          } else {
            tableApiRef.current?.hideOverlay();
          }

          params.successCallback(setupList, Number(totalElements));

          if (isAssetTemplateV2Enabled && hasPollingStateRows(setupList)) {
            startPolling();
          }
        } catch (error) {
          if (!isCanceledError(error)) {
            params.failCallback();
          }
        }
      },
    };
  };

  const getTableApi = (tableApi: GridApi): void => {
    tableApiRef.current = tableApi;

    const getTableDataSource = getTableDataSourceRef.current;
    if (isAssetTemplateV2EnabledRef.current === null || !getTableDataSource) {
      tableApi.showLoadingOverlay();
      return;
    }

    tableApi.setDatasource(getTableDataSource());
  };

  const refreshTable = useCallback((): void => {
    const gridApi = tableApiRef.current;
    if (!gridApi) {
      return;
    }

    gridApi.purgeInfiniteCache();

    if (isAssetTemplateV2Enabled) {
      startPolling();
    }
  }, [isAssetTemplateV2Enabled, startPolling]);

  const onPageSizeChange = (numberOfItems: number): void => {
    setPageSize(numberOfItems);
    Session.setPageSizeByKey(pageSizeKey, numberOfItems);
  };

  useEffect(() => {
    const gridApi = tableApiRef.current;
    const buildDataSource = getTableDataSourceRef.current;
    if (!gridApi || !buildDataSource || isAssetTemplateV2Enabled === null) {
      return;
    }

    gridApi.setDatasource(buildDataSource());
  }, [isAssetTemplateV2Enabled]);

  return {
    tableApiRef,
    getTableApi,
    onPageSizeChange,
    refreshTable,
    pageSize,
  };
};
